import { useFormik } from 'formik';
import { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { InferType, object, string } from 'yup';
import { toast } from 'react-toastify';
import { Input } from '../../../components/common/Form';
import ReservationOrderInputLabel from '../../../components/ReservationOrderInputLabel';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import { updateURL } from '../../../api/restaurant';
import { openModal, useModalContext } from '../../../contexts/ModalContext';
import { handleHttpsInput } from '../../../utils/general';
import RestaurantWizardHeader from '../RestaurantWizard/RestaurantWizardHeader';
import LocationProfileActions from '../LocationProfile/LocationProfileActions';

const ReservationInputSchema = object({
  reservation: string().default('')
});

const ReservationOrderPage = () => {
  const { restaurantID } = useParams<{ restaurantID: string }>();
  const { currentRestaurant, setRestaurant, updateRestaurant } = useRestaurantContext();

  useEffect(() => {
    if (restaurantID && currentRestaurant?.restaurantID !== Number(restaurantID)) {
      setRestaurant(Number(restaurantID));
    }
  }, [restaurantID, currentRestaurant?.restaurantID, setRestaurant]);
  const { dispatch } = useModalContext();

  const handleOnSubmit = async (value: InferType<typeof ReservationInputSchema>) => {
    const { reservationUrl } = currentRestaurant;
    let tempReservationUrl = null;

    if (value.reservation !== reservationUrl) {
      tempReservationUrl = value.reservation;
    }

    if (tempReservationUrl !== null) {
      try {
        await updateURL(tempReservationUrl);
        updateRestaurant({
          ...currentRestaurant,
          reservationUrl: tempReservationUrl != null ? tempReservationUrl : reservationUrl
        });
        toast.success('Successfully Updated!', {
          position: toast.POSITION.TOP_RIGHT
        });
      } catch (error) {
        openModal({
          dispatch,
          message:
            'An unexpected error has occurred. Please email our team at info@taptabapp.com so that we can address this issue promptly.'
        });
      }
    }
  };

  const initialValues = useMemo(
    () => ({
      reservation: currentRestaurant?.reservationUrl || ''
    }),
    [currentRestaurant]
  );

  const formik = useFormik({
    initialValues,
    validationSchema: ReservationInputSchema,
    onSubmit: handleOnSubmit,
    enableReinitialize: true
  });

  const handleInputChange = (name: string, value: string) => {
    const modifiedValue = handleHttpsInput(value);
    formik.setFieldValue(name, modifiedValue);
  };

  return (
    <div className="restaurant-wizard-container reservation-order-container">
      <div className="restaurant-wizard-hero-background" aria-hidden="true" />
      <div className="restaurant-wizard-scroll-body">
        <RestaurantWizardHeader title="RESERVATIONS" />
        <form className="restaurant-wizard-content reservation-order-content" onSubmit={formik.handleSubmit}>
          <div className="reservation-order-page">
            <div className="reservation-order-inputs">
              <Input
                name="reservation"
                label={
                  <ReservationOrderInputLabel
                    label="reservations"
                    text="Enter the link at which guests can make reservations."
                  />
                }
                className="reservation-order-input"
                onChange={(event: any) => handleInputChange('reservation', event.target.value)}
                onBlur={formik.handleBlur}
                value={formik.values.reservation}
                touched={formik.touched.reservation}
                error={formik.errors.reservation}
              />
            </div>
            <LocationProfileActions submit isSaving={formik.isSubmitting} />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReservationOrderPage;
